import {useCallback, useMemo, useState} from 'react'

import type {PreviewQueryContextValue} from '../contexts/PreviewQueryContext'
import type {UserShortcut} from '../types'
import type {EditableShortcutField} from './use-shortcut-editor'

interface UseSavedViewEditorOptions {
  initialShortcut: UserShortcut | (() => UserShortcut)
}

export interface UseSavedViewEditorReturn {
  shortcut: UserShortcut
  setShortcutValue: (field: EditableShortcutField, value: string) => void
  previewQuery: string
  commitPreviewQuery: (next: string) => void
  previewQueryContextValue: PreviewQueryContextValue
  isDirty: boolean
}

export function useSavedViewEditor({initialShortcut: initialShortcutProp}: UseSavedViewEditorOptions) {
  const [initialShortcut] = useState<UserShortcut>(initialShortcutProp)
  const [shortcut, setShortcut] = useState<UserShortcut>(initialShortcut)
  const [previewQuery, setPreviewQuery] = useState(() => initialShortcut.query.trim())

  const setShortcutValue = useCallback((field: EditableShortcutField, value: string) => {
    setShortcut(previous => ({...previous, [field]: value}))
  }, [])

  const commitPreviewQuery = useCallback((next: string) => {
    const trimmed = next.trim()
    setShortcut(previous => (previous.query === trimmed ? previous : {...previous, query: trimmed}))
    setPreviewQuery(trimmed)
  }, [])

  const previewQueryContextValue = useMemo(
    () => ({currentQuery: previewQuery, onQueryChange: commitPreviewQuery}),
    [previewQuery, commitPreviewQuery],
  )

  const isDirty =
    shortcut.name !== initialShortcut.name ||
    (shortcut.description ?? '') !== (initialShortcut.description ?? '') ||
    shortcut.query !== initialShortcut.query ||
    shortcut.icon !== initialShortcut.icon ||
    shortcut.color !== initialShortcut.color

  return {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue, isDirty}
}
